import { useState } from "react";

import { AppNavigation, AppStepper, TierStepper } from "@zenith/hooks";
import Clock from "@zenith/assets/icons/Clock";
import { ModalComponent, MajorButtonComponent } from "@zenith/components";
import QRCode from "react-qr-code";
import { GreenTick } from "@zenith/assets/icons/components";
import { SquaredError } from "@zenith/assets/icons/SquaredError";
import { ArchiveTick } from "@zenith/assets/icons/ArchiveTick";
import { UPGRADE_ACCOUNT } from "@zenith/routes/paths";
import {
  ApiResponseDTODiscardUpgradeResponseDTO,
  TierApiService,
  TierStepsResponseDTO,
} from "@zenith/services/user";
import { useUpgradeContext } from "@zenith/context";
import StepStatusComponent from "@zenith/features/Tiers/components/StepStatusComponent/step-status-component/step-status-component";

export interface MobileOnlyStepPageProps {
  step: TierStepsResponseDTO;
  stepTitle?: string;
  message?: string;
  mobileDeepLink?: string;
}

function MobileOnlyStepPage(props: MobileOnlyStepPageProps) {
  const {
    step: { stepStatus },
    message = "This step can only be completed on our Finopay Mobile Application because we require your location to complete this process. Kindly log into the Finopay app on your mobile phone to Complete your address verification.",
    mobileDeepLink,
  } = props;

  const { setActiveStep } = AppStepper();
  const { goToNextOpenStep } = TierStepper();
  const { navigateTo } = AppNavigation();
  const { clearUpgradeContext } = useUpgradeContext();
  const [backModal, setBackModal] = useState<boolean>(false);

  const handleModalState = () => {
    setBackModal(!backModal);
  };

  const handleContinueButton = () => {
    goToNextOpenStep();
  };

  const handleDiscardUpgrade = () => {
    TierApiService.discardUpgradeTier()
      .then((response: ApiResponseDTODiscardUpgradeResponseDTO) => {
        if ("data" in response && response.data) {
          setActiveStep(0);
          clearUpgradeContext();
          navigateTo(UPGRADE_ACCOUNT);
        }
      })
      .catch((error) => {
        return error;
      });
  };

  const handleOpenApp = () => {
    if (mobileDeepLink) window.open(mobileDeepLink, "_blank");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const isApproved = stepStatus === TierStepsResponseDTO.stepStatus.APPROVED;
  const isPending = stepStatus === TierStepsResponseDTO.stepStatus.PENDING;

  const bulletPoints: { text: React.ReactNode }[] = [
    { text: "Download the Finopay app if you haven't already." },
    {
      text: (
        <>
          Complete{" "}
          <strong className="font-semibold">address verification</strong> within
          the app
        </>
      ),
    },
    {
      text: (
        <>
          You can choose to{" "}
          <strong className="font-semibold ">return here</strong> to continue or{" "}
          <strong className="font-semibold">continue from there.</strong>
        </>
      ),
    },
    {
      text: (
        <>
          Already done with the verification on Mobile? Click{" "}
          <strong className="font-semibold text-indigo-600">Refresh</strong>{" "}
          below to continue.
        </>
      ),
    },
  ];

  return (
    <>
      <ModalComponent
        body="Are you sure you want to leave?"
        secondLine="All information provided will be lost."
        title="Discard Upgrade"
        showModal={backModal}
        onClickClose={handleModalState}
        doubleOptions={true}
        onLeftButtonClick={handleDiscardUpgrade}
        onRightButtonClick={handleModalState}
        leftButtonText="Discard Upgrade"
        rightButtonText="Return to Upgrade"
      />

      <div className="mb-5">
        {(isApproved || isPending) && (
          <div className="mt-6 flex flex-col">
            <h1 className="mb-16 text-4.5xl font-medium text-neutral-900">
              Business Address
            </h1>

            {isApproved && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.APPROVED}
                title={"Information Successfully Verified"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<GreenTick transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}

            {isPending && (
              <StepStatusComponent
                status={TierStepsResponseDTO.stepStatus.PENDING}
                title={"Waiting For Approval"}
                subTitle={'Press "Continue" to proceed.'}
                icon={<Clock transform="scale(1.70)" />}
                handleDiscardButton={handleModalState}
                handleContinueButton={handleContinueButton}
              />
            )}
          </div>
        )}

        {!isApproved && !isPending && (
          <div className="flex max-w-2xl flex-col">
            {/* Warning icon */}
            <div className="mb-6 flex h-[57px] w-[57px] items-center justify-center rounded-2xl bg-[#F3F3F3] shadow-sm">
              <SquaredError width={27.06} height={25.97} color="#000" />
            </div>

            {/* Title */}
            <h1 className="mb-4 w-full text-2xl font-bold leading-tight text-neutral-900 sm:text-[40px]">
              Complete
              <br />
              Verification on App
            </h1>

            {/* Description */}
            <p className="mb-6 text-sm leading-relaxed text-neutral-600 sm:text-[16px]">
              {message}
            </p>

            {/* Info card */}
            <div className=" mb-8 rounded-[8px] bg-[#5656CA14] p-5 sm:-mr-6">
              <ul className="flex flex-col gap-4">
                {bulletPoints.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ArchiveTick width={17.5} height={17.5} />
                    <span className="text-sm leading-relaxed text-accent-info sm:text-base">
                      {item.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col gap-3 sm:-mr-6 sm:flex-row">
              <MajorButtonComponent
                id="open-app-btn"
                type="button"
                buttonType="primary"
                onClick={handleOpenApp}
                widerButton
                className="w-full flex-1 !whitespace-nowrap"
              >
                Open Mobile Application
              </MajorButtonComponent>
              <MajorButtonComponent
                id="refresh-btn"
                type="button"
                buttonType="secondary"
                onClick={handleRefresh}
                widerButton
                className="w-full flex-1"
              >
                Refresh
              </MajorButtonComponent>
            </div>

            {/* QR code */}
            {mobileDeepLink && (
              <div className="mt-10 flex flex-col items-center">
                <p className="mb-3 text-base text-neutral-600">
                  Scan to download app
                </p>
                <div className="rounded-lg bg-white p-3 shadow-sm">
                  <QRCode value={mobileDeepLink} size={160} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default MobileOnlyStepPage;
